import React, { MouseEvent, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useReactiveVar } from "@apollo/client"
import { Loading } from "components/Loading"
import options from "contexts/options.json"
import { FilterCheck, FilterInput } from "components/UIParts"
import { filterStates } from "database/AppApolloProvider"
import { useMongoQueries } from "contexts/useMongoQuery"
import { DataTable, TableHeadBase, createColumnsFromHeads } from "components/DataTable"

const createHeads = <T extends readonly TableHeadBase[]>(configs: T): T => configs

const heads = createHeads([
    {
        field: "isTemp",
        text: "仮登録",
        cell: (row: KV) =>
            row.isTemp ? (
                <span className="px-1 rounded bg-red-300 text-white text-xs">仮登録</span>
            ) : (
                ""
            ),
    },
    { field: "patientId", text: "患者ID" },
    { field: "name", text: "氏名" },
    { field: "nameKana", text: "氏名カナ" },
    { field: "gender", text: "性別", type: "Option", optionKey: "genderType" },
    { field: "birthDate", text: "生年月日", type: "Date" },
    { field: "birthDate", text: "年齢", type: "Age" },
    { field: "tel", text: "電話番号" },
    { field: "mobileTel", text: "携帯番号" },
    { field: "email", text: "メール" },
])

const getQueryVariables = (values: KV) => {
    let output: KV = {}
    if (values.isTemp !== undefined && values.isTemp !== null) output.isTemp = values.isTemp
    if (values.patientId) output.patientId = values.patientId
    if (values.name) output.name = values.name
    if (values.nameKana) output.nameKana = values.nameKana
    return output
}

export const List = () => {
    const navigate = useNavigate()
    const values: KV = useReactiveVar(filterStates)
    const names = values.patientList || { isTemp: false, patientId: "", name: "", nameKana: "" }
    const { loading, data } = useMongoQueries("patients", getQueryVariables(names))
    const rows: KV[] = data || []
    const columns = useMemo(() => createColumnsFromHeads(heads), [])

    const setValue = (name: string, value: any) => {
        filterStates({ ...filterStates(), patientList: { ...names, [name]: value } })
    }

    const handleClick = (e: MouseEvent<HTMLButtonElement | HTMLTableRowElement>) => {
        e.preventDefault()
        e.stopPropagation()
        navigate(`/patient/edit/${e.currentTarget.dataset.id}`)
    }

    if (loading) {
        return <Loading full />
    }

    const filterContent = (
        <>
            <FilterCheck label="仮登録のみ" name="isTemp" value={names.isTemp} setValue={setValue} />
            <FilterInput label="患者ID" name="patientId" value={names.patientId} setValue={setValue} />
            <FilterInput label="患者名" name="name" value={names.name} setValue={setValue} />
            <FilterInput label="患者名カナ" name="nameKana" value={names.nameKana} setValue={setValue} />
        </>
    )

    return (
        <DataTable<KV>
            data={rows}
            columns={columns}
            filterContent={filterContent}
            itemCount={rows.length}
            getRowProps={row => ({
                key: (row.original as KV)._id,
                'data-id': (row.original as KV)._id,
                className: 'text-sm text-gray-900 cursor-pointer hover:bg-theme-50',
                onClick: handleClick,
            })}
            storageKey="patientList_columnSettings"
        />
    )
}

export default List